import { useState, useEffect } from 'react';
import { Search, ChevronDown, ChevronUp, Crown } from 'lucide-react';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { AVAILABLE_TASKS, TASK_CATEGORIES, TASK_CATEGORY_INFO } from '../../types/taskTypes';
import { useAuth } from '../../context/AuthContext';
import { userApi } from '../../services/api';
import { useNotificationStore } from '../../stores';

interface TaskSelectionStepProps {
  onSelectTask: (taskId: string) => void;
  selectedTask?: string;
}

export function TaskSelectionStep({ onSelectTask, selectedTask }: TaskSelectionStepProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>(TASK_CATEGORIES.ALL);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set(['all']));

  // Account info for premium checking
  const { user } = useAuth();
  const { addNotification } = useNotificationStore();
  const [accountInfo, setAccountInfo] = useState<any>(null);

  // Fetch account info when component mounts
  useEffect(() => {
    const fetchAccountInfo = async () => {
      if (!user) return;
      try {
        const response = await userApi.fetchAccountInfo();
        if (response && !response.error) {
          setAccountInfo(response);
        }
      } catch (error) {
        console.error('Failed to fetch account info:', error);
      }
    };
    fetchAccountInfo();
  }, [user]);

  // Filter tasks that are not hidden and not coming soon
  const availableTasks = AVAILABLE_TASKS.filter(task => !task.hidden && !task.coming_soon);

  // Map task_type to category
  const getTaskCategory = (taskType?: string): string => {
    if (!taskType) return TASK_CATEGORIES.LEGACY;

    if (taskType === 'legacy') return TASK_CATEGORIES.LEGACY;
    if (taskType.startsWith('ir_')) return TASK_CATEGORIES.INVENTION_RECORD;
    if (taskType.startsWith('draft_')) return TASK_CATEGORIES.DRAFT_PROCESSING;
    if (taskType.startsWith('form_')) return TASK_CATEGORIES.FORM_PROCESSING;
    if (taskType.startsWith('oa_')) return TASK_CATEGORIES.OFFICE_ACTION;
    if (taskType.startsWith('translate_') || taskType === 'latex_to_docx' || taskType === 'ocr_pdf') {
      return TASK_CATEGORIES.TRANSLATION;
    }

    return TASK_CATEGORIES.LEGACY;
  };

  // Filter tasks by search and category
  const filteredTasks = availableTasks.filter(task => {
    const matchesSearch = !searchQuery ||
      task.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      task.description.toLowerCase().includes(searchQuery.toLowerCase());

    const taskCategory = getTaskCategory(task.task_type);
    const matchesCategory = selectedCategory === TASK_CATEGORIES.ALL ||
      taskCategory === selectedCategory;

    return matchesSearch && matchesCategory;
  });

  // Group tasks by category
  const tasksByCategory: Record<string, typeof availableTasks> = {};
  availableTasks.forEach(task => {
    const category = getTaskCategory(task.task_type);
    if (!tasksByCategory[category]) {
      tasksByCategory[category] = [];
    }
    tasksByCategory[category].push(task);
  });

  const toggleCategory = (category: string) => {
    setExpandedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(category)) {
        newSet.delete(category);
      } else {
        newSet.add(category);
      }
      return newSet;
    });
  };

  // Get unique categories from available tasks
  const categories = Object.keys(tasksByCategory).map(categoryKey => {
    const info = TASK_CATEGORY_INFO[categoryKey] || {
      name: categoryKey,
      icon: null,
      color: 'gray'
    };
    return {
      key: categoryKey,
      ...info,
      count: tasksByCategory[categoryKey].length
    };
  });

  return (
    <div className="space-y-4">
      <div className="text-center space-y-2">
        <h2 className="text-2xl font-bold text-gray-900">Browse All Tasks</h2>
        <p className="text-sm text-gray-600">
          Select a task to get started - all {availableTasks.length} available tasks shown below
        </p>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <Input
          type="text"
          placeholder="Search tasks..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Category Filters */}
      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => setSelectedCategory(TASK_CATEGORIES.ALL)}
          className={`px-3 py-1 text-xs rounded-full border transition-colors ${
            selectedCategory === TASK_CATEGORIES.ALL
              ? 'bg-blue-100 border-blue-500 text-blue-700'
              : 'bg-gray-100 border-gray-300 text-gray-700 hover:bg-gray-200'
          }`}
        >
          All ({availableTasks.length})
        </button>
        {categories.map(category => {
          const Icon = category.icon;
          return (
            <button
              key={category.key}
              onClick={() => setSelectedCategory(category.key)}
              className={`px-3 py-1 text-xs rounded-full border transition-colors flex items-center gap-1 ${
                selectedCategory === category.key
                  ? 'bg-blue-100 border-blue-500 text-blue-700'
                  : 'bg-gray-100 border-gray-300 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {Icon && <Icon className="w-3 h-3" />}
              {category.name} ({category.count})
            </button>
          );
        })}
      </div>

      {/* Tasks List */}
      <div className="max-h-[400px] overflow-y-auto border rounded-lg">
        {selectedCategory === TASK_CATEGORIES.ALL ? (
          // Show tasks grouped by category
          <div className="divide-y">
            {categories.map(category => {
              const Icon = category.icon;
              const isExpanded = expandedCategories.has(category.key);
              const categoryTasks = tasksByCategory[category.key].filter(task => {
                if (!searchQuery) return true;
                return task.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                  task.description.toLowerCase().includes(searchQuery.toLowerCase());
              });

              if (categoryTasks.length === 0) return null;

              return (
                <div key={category.key}>
                  <button
                    onClick={() => toggleCategory(category.key)}
                    className="w-full p-3 flex items-center justify-between hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      {Icon && <Icon className="w-4 h-4 text-gray-600" />}
                      <span className="font-medium text-sm text-gray-900">{category.name}</span>
                      <Badge variant="secondary" className="text-xs">
                        {categoryTasks.length}
                      </Badge>
                    </div>
                    {isExpanded ? (
                      <ChevronUp className="w-4 h-4 text-gray-400" />
                    ) : (
                      <ChevronDown className="w-4 h-4 text-gray-400" />
                    )}
                  </button>
                  {isExpanded && (
                    <div className="bg-gray-50">
                      {categoryTasks.map(task => {
                        const TaskIcon = task.icon;
                        const isSelected = selectedTask === task.id;
                        return (
                          <button
                            key={task.id}
                            onClick={() => {
                              // Check if task is premium-only and user is not premium
                              if (task.premium_only && accountInfo?.account_type !== 'premium') {
                                addNotification({
                                  type: 'warning',
                                  title: 'Premium Feature',
                                  message: `${task.name} is only available for Premium subscribers. Upgrade to access this feature.`,
                                  duration: 5000,
                                });
                                return;
                              }
                              onSelectTask(task.id);
                            }}
                            className={`w-full p-3 text-left border-l-2 transition-all ${
                              isSelected
                                ? 'bg-blue-50 border-blue-500'
                                : 'border-transparent hover:bg-white hover:border-gray-300'
                            }`}
                          >
                            <div className="flex items-start gap-3">
                              {TaskIcon && (
                                <div className="w-8 h-8 bg-white rounded flex items-center justify-center flex-shrink-0 shadow-sm">
                                  <TaskIcon className="w-4 h-4 text-gray-700" />
                                </div>
                              )}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <h4 className="font-medium text-sm text-gray-900">{task.name}</h4>
                                  {isSelected && (
                                    <span className="text-xs bg-blue-500 text-white px-2 py-0.5 rounded-full">
                                      Selected
                                    </span>
                                  )}
                                  {task.premium_only && accountInfo?.account_type !== 'premium' && (
                                    <Badge variant="outline" className="bg-amber-50 text-amber-800 border-amber-200 text-xs">
                                      <Crown className="w-3 h-3 mr-1 inline" />
                                      Premium
                                    </Badge>
                                  )}
                                </div>
                                <p className="text-xs text-gray-600 mt-0.5">{task.description}</p>
                              </div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          // Show tasks in a flat list for specific category
          <div className="divide-y">
            {filteredTasks.map(task => {
              const TaskIcon = task.icon;
              const isSelected = selectedTask === task.id;
              return (
                <button
                  key={task.id}
                  onClick={() => {
                    // Check if task is premium-only and user is not premium
                    if (task.premium_only && accountInfo?.account_type !== 'premium') {
                      addNotification({
                        type: 'warning',
                        title: 'Premium Feature',
                        message: `${task.name} is only available for Premium subscribers. Upgrade to access this feature.`,
                        duration: 5000,
                      });
                      return;
                    }
                    onSelectTask(task.id);
                  }}
                  className={`w-full p-3 text-left border-l-2 transition-all ${
                    isSelected
                      ? 'bg-blue-50 border-blue-500'
                      : 'border-transparent hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    {TaskIcon && (
                      <div className="w-8 h-8 bg-white rounded flex items-center justify-center flex-shrink-0 shadow-sm">
                        <TaskIcon className="w-4 h-4 text-gray-700" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h4 className="font-medium text-sm text-gray-900">{task.name}</h4>
                        {isSelected && (
                          <span className="text-xs bg-blue-500 text-white px-2 py-0.5 rounded-full">
                            Selected
                          </span>
                        )}
                        {task.premium_only && accountInfo?.account_type !== 'premium' && (
                          <Badge variant="outline" className="bg-amber-50 text-amber-800 border-amber-200 text-xs">
                            <Crown className="w-3 h-3 mr-1 inline" />
                            Premium
                          </Badge>
                        )}
                      </div>
                      <p className="text-xs text-gray-600 mt-0.5">{task.description}</p>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}

        {filteredTasks.length === 0 && (
          <div className="p-8 text-center text-gray-500">
            <p className="text-sm">No tasks found matching your search</p>
          </div>
        )}
      </div>

      <div className="text-center text-xs text-gray-500">
        {selectedTask ? 'Click Next to continue' : 'Select a task to get started'}
      </div>
    </div>
  );
}
